import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import './brand-page.css';
import './brand-kit.css';
import SafeIcon from './SafeIcon';
import type { IconName } from './SafeIcon';
import FontPicker from './FontPicker';
import ColourEditor from './ColourEditor';
import MaterialPicker from './MaterialPicker';
import type { PickedItem } from './MaterialPicker';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { BrandAsset, BrandColour, BrandFont, BrandSettings } from '../store/seed';
import { fontStack, googleFonts, loadFont } from '../data/googleFonts';

const RGB_CHANNEL_OFFSETS_IN_HEX = [1, 3, 5];
const WCAG_AA_NORMAL_TEXT_CONTRAST = 4.5;
const WCAG_AA_LARGE_TEXT_CONTRAST = 3;
const DEMO_YEAR = 2026;
const NOVEMBER = 10;
const DEMO_DAY_FOR_NEW_EXAMPLES = 18;
const FONT_CHECK_FALLBACK_DELAY_MS = 2000;
const PHONE_LAYOUT_MEDIA_QUERY = '(max-width: 767px)';

const hexChannelValues = (hex: string) =>
  RGB_CHANNEL_OFFSETS_IN_HEX.map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

function wcagRelativeLuminance(hex: string) {
  const [red, green, blue] = hexChannelValues(hex)
    .map((channelValue) => channelValue / 255)
    .map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}
function contrastRatio(firstHex: string, secondHex: string) {
  const [lighter, darker] = [
    wcagRelativeLuminance(firstHex),
    wcagRelativeLuminance(secondHex),
  ].sort((left, right) => right - left);
  return (lighter + 0.05) / (darker + 0.05);
}
const rgbText = (hex: string) => hexChannelValues(hex).join(', ');
const demoNovemberDate = (day: number) => new Date(DEMO_YEAR, NOVEMBER, day);

type ContrastLevel = 'pass' | 'large' | 'fail';
const contrastLevelFor = (ratio: number): ContrastLevel => {
  if (ratio >= WCAG_AA_NORMAL_TEXT_CONTRAST) return 'pass';
  if (ratio >= WCAG_AA_LARGE_TEXT_CONTRAST) return 'large';
  return 'fail';
};
const contrastLevelStatusClass: Record<ContrastLevel, string> = {
  pass: 'published',
  large: 'review',
  fail: 'input',
};
const contrastLevelIcon: Record<ContrastLevel, IconName> = {
  pass: 'FiCheckCircle',
  large: 'FiAlertTriangle',
  fail: 'FiXCircle',
};

const assetSourceIcon = (source: BrandAsset['source']): IconName => {
  if (source === 'Drive') return 'FiLink';
  if (source === 'Uploaded') return 'FiUpload';
  return 'FiImage';
};

const brandAssetSourceFor = (pickedSource: PickedItem['source']): BrandAsset['source'] => {
  if (pickedSource === 'Upload') return 'Uploaded';
  if (pickedSource === 'Drive') return 'Drive';
  return 'Material bank';
};

const assetKindForPurpose = (
  purpose: string,
  currentKind: BrandAsset['kind']
): BrandAsset['kind'] => {
  if (purpose === 'Icon') return 'Icon';
  if (purpose === 'Menu') return 'Document';
  if (purpose === 'Storefront') return 'Photo';
  return currentKind === 'Document' ? 'Document' : 'Logo';
};

const MAX_NOTES = 2000;
const NOTES_NEAR_LIMIT = MAX_NOTES * 0.9;
const toneOptions = [
  {
    id: 'warm',
    label: 'Calm and warm',
    example: 'Slow mornings are better with a good cup. Come in, sit by the window.',
  },
  {
    id: 'friendly',
    label: 'Friendly and relaxed',
    example: 'New on the menu: gingerbread latte. You know what to do ☕',
  },
  {
    id: 'professional',
    label: 'Clear and professional',
    example: 'Our winter menu is available from 16 November, daily 8:00–19:00.',
  },
  {
    id: 'playful',
    label: 'Playful',
    example: 'Warning: this gingerbread latte may cause spontaneous cosiness.',
  },
];
const choiceGroups = [
  {
    key: 'emoji' as const,
    label: 'Emoji',
    options: [
      ['none', 'None'],
      ['few', 'A few (1–2)'],
      ['many', 'Plenty'],
    ],
  },
  {
    key: 'hashtags' as const,
    label: 'Hashtags',
    options: [
      ['none', 'None'],
      ['branded', 'Our own (2)'],
      ['mixed', 'Own and popular (3–5)'],
    ],
  },
  {
    key: 'cta' as const,
    label: 'Call to action',
    options: [
      ['invite', 'Gentle invitation'],
      ['direct', 'Clear and direct'],
      ['none', 'No call to action'],
    ],
  },
];
const assetPurposes = [
  'Main logo',
  'For dark backgrounds',
  'Icon',
  'Menu',
  'Brand reference',
  'Storefront',
];
const optionLabel = (key: string, value: string) =>
  choiceGroups
    .find((group) => group.key === key)
    ?.options.find((option) => option[0] === value)?.[1] ?? value;

type AssetPreviewBackground = 'dark' | 'light' | 'checker';
type DraftChange = {
  label: string;
  key: string;
  variables?: Record<string, string>;
  from: string;
  to: string;
  swatches?: [string | undefined, string | undefined];
};

function describeDraftChanges(
  active: BrandSettings,
  draft: BrandSettings | null,
  translate: (key: string, vars?: Record<string, string | number>) => string
): DraftChange[] {
  if (!draft) return [];
  const draftChanges: DraftChange[] = [];
  const labelKeepingUntranslatedKeyForHistory = (key: string, vars?: Record<string, string>) => ({
    label: translate(key, vars),
    key,
    vars,
  });
  const hasSameContent = (first: unknown, second: unknown) =>
    JSON.stringify(first) === JSON.stringify(second);
  const toneLabels = (ids: string[]) =>
    ids
      .map((id) => translate(toneOptions.find((option) => option.id === id)?.label ?? id))
      .join(', ') || '—';
  (
    [
      ['positioning', 'In one sentence'],
      ['audience', 'Who we talk to'],
      ['products', 'Key products'],
      ['facts', 'Verified facts'],
    ] as const
  ).forEach(([key, label]) => {
    if (active[key] !== draft[key])
      draftChanges.push({
        ...labelKeepingUntranslatedKeyForHistory(label),
        from: active[key],
        to: draft[key],
      });
  });
  draft.colours.forEach((colour) => {
    const before = active.colours.find((item) => item.name === colour.name);
    if (!before)
      draftChanges.push({
        ...labelKeepingUntranslatedKeyForHistory('Colour added: {name}', { name: colour.name }),
        from: '—',
        to: `${colour.hex} · ${translate(colour.role)}`,
        swatches: [undefined, colour.hex],
      });
    else if (!hasSameContent(before, colour))
      draftChanges.push({
        ...labelKeepingUntranslatedKeyForHistory('Colour changed: {name}', { name: colour.name }),
        from: `${before.hex} · ${translate(before.role)}`,
        to: `${colour.hex} · ${translate(colour.role)}`,
        swatches: [before.hex, colour.hex],
      });
  });
  active.colours
    .filter((colour) => !draft.colours.some((item) => item.name === colour.name))
    .forEach((colour) =>
      draftChanges.push({
        ...labelKeepingUntranslatedKeyForHistory('Colour removed: {name}', { name: colour.name }),
        from: colour.hex,
        to: '—',
        swatches: [colour.hex, undefined],
      })
    );
  const fontSummary = (items: BrandFont[]) =>
    items.map((font) => `${font.family} (${translate(font.role)})`).join(', ');
  if (!hasSameContent(active.fonts, draft.fonts))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Fonts'),
      from: fontSummary(active.fonts),
      to: fontSummary(draft.fonts),
    });
  const assetSummary = (items: BrandAsset[]) =>
    items.map((asset) => `${translate(asset.label)}: ${asset.file}`).join(', ');
  if (!hasSameContent(active.assets, draft.assets))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Brand assets'),
      from: assetSummary(active.assets),
      to: assetSummary(draft.assets),
    });
  if (!hasSameContent(active.photoStyle, draft.photoStyle))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Photo style'),
      from: active.photoStyle.join(' · '),
      to: draft.photoStyle.join(' · '),
    });
  if (!hasSameContent(active.logoRules, draft.logoRules))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Logo use'),
      from: active.logoRules.join(' · '),
      to: draft.logoRules.join(' · '),
    });
  if (!hasSameContent(active.languages, draft.languages))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Content languages'),
      from: active.languages.map((language) => `${language.code}: ${language.note}`).join(' · '),
      to: draft.languages.map((language) => `${language.code}: ${language.note}`).join(' · '),
    });
  if (active.tones.join() !== draft.tones.join())
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Tone of voice'),
      from: toneLabels(active.tones),
      to: toneLabels(draft.tones),
    });
  (['emoji', 'hashtags', 'cta'] as const).forEach((key) => {
    if (active[key] !== draft[key])
      draftChanges.push({
        ...labelKeepingUntranslatedKeyForHistory(
          choiceGroups.find((group) => group.key === key)!.label
        ),
        from: translate(optionLabel(key, active[key])),
        to: translate(optionLabel(key, draft[key])),
      });
  });
  if (active.words.use.join() !== draft.words.use.join())
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Words we use'),
      from: active.words.use.join(', '),
      to: draft.words.use.join(', '),
    });
  if (active.words.avoid.join() !== draft.words.avoid.join())
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Words we avoid'),
      from: active.words.avoid.join(', '),
      to: draft.words.avoid.join(', '),
    });
  if (active.notes !== draft.notes)
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Anything else AMY should know'),
      from: active.notes,
      to: draft.notes,
    });
  if (!hasSameContent(active.examples, draft.examples))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Good examples'),
      from: String(active.examples.length),
      to: String(draft.examples.length),
    });
  if (!hasSameContent(active.avoid, draft.avoid))
    draftChanges.push({
      ...labelKeepingUntranslatedKeyForHistory('Avoid'),
      from: active.avoid
        .filter((avoidItem) => avoidItem.rule)
        .map((avoidItem) => avoidItem.text)
        .join(' · '),
      to: draft.avoid
        .filter((avoidItem) => avoidItem.rule)
        .map((avoidItem) => avoidItem.text)
        .join(' · '),
    });
  return draftChanges;
}

function Section({
  id,
  icon,
  title,
  intro,
  action,
  children,
  defaultOpen = true,
}: {
  id: string;
  icon: IconName;
  title: string;
  intro?: string;
  action?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="panel brand-section" id={id} open={defaultOpen}>
      <summary>
        <span className="brand-section-icon">
          <SafeIcon name={icon} />
        </span>
        <span>
          <h2>{title}</h2>
          {intro && <span className="muted">{intro}</span>}
        </span>
        <SafeIcon name="FiChevronDown" className="brand-section-chevron" />
      </summary>
      <div className="brand-section-body">
        {action && <div className="brand-section-action">{action}</div>}
        {children}
      </div>
    </details>
  );
}

function ListEditor({
  items,
  canEdit,
  onChange,
  placeholder,
  label,
}: {
  items: string[];
  canEdit: boolean;
  onChange: (items: string[]) => void;
  placeholder: string;
  label: string;
}) {
  const { translate } = useApp();
  const [value, setValue] = useState('');
  return (
    <>
      <ul className="brand-list editable">
        {items.map((item) => (
          <li key={item}>
            <span>{item}</span>
            {canEdit && (
              <button
                className="icon-button small-icon"
                aria-label={translate('Remove {word}', { word: item })}
                onClick={() => onChange(items.filter((otherItem) => otherItem !== item))}
              >
                <SafeIcon name="FiX" size={13} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form
          className="brand-word-add"
          onSubmit={(event) => {
            event.preventDefault();
            if (value.trim() && !items.includes(value.trim())) {
              onChange([...items, value.trim()]);
              setValue('');
            }
          }}
        >
          <input
            className="text-field"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            aria-label={label}
          />
          <button className="button small" type="submit">
            <SafeIcon name="FiPlus" size={14} />
            {translate('Add')}
          </button>
        </form>
      )}
    </>
  );
}

type Dialog =
  | { kind: 'font'; role: BrandFont['role']; replacing?: BrandFont; note?: string }
  | { kind: 'colour'; colour?: BrandColour }
  | { kind: 'pickAsset'; replaceId?: string }
  | { kind: 'asset'; asset: BrandAsset; isNew?: boolean }
  | { kind: 'example'; index: number }
  | { kind: 'addExample' }
  | { kind: 'review' }
  | { kind: 'restore'; version: number }
  | null;

export default function BrandPage() {
  const { translate, readOnlyProps, readOnly, language } = useApp();
  const { route, role, actor, notify, showPlannedFeatureNotice, navigate, openChat } = useUi();
  const { commands } = useDemo();
  const { workspace, brand, posts, business, settings, members } = useWorkspaceData(
    route.workspaceId
  );
  const shownBrand = brand.draft ?? brand.active;
  const currentMember = members.find((member) => member.name === actor.name);
  const contributorMayEditDraft =
    role === 'contributor' &&
    (actor.name === workspace.contributor
      ? !!settings.contributorCanEditBrand
      : !!currentMember?.canEditBrand);
  const canEdit = !readOnly && (role === 'owner' || role === 'agency' || contributorMayEditDraft);
  const canApply = !readOnly && role === 'owner';
  const isContributor = role === 'contributor';
  const editLockProps = canEdit
    ? {}
    : {
        disabled: true,
        title: isContributor
          ? translate('Ask {name} to change this, or suggest a change.', { name: workspace.owner })
          : translate('Read-only view'),
      };
  const changes = useMemo(
    () => describeDraftChanges(brand.active, brand.draft, translate),
    [brand.active, brand.draft, translate]
  );
  const isPhone =
    typeof window !== 'undefined' && window.matchMedia(PHONE_LAYOUT_MEDIA_QUERY).matches;

  const [assetPreviewBackground, setAssetPreviewBackground] =
    useState<AssetPreviewBackground>('checker');
  const [newWord, setNewWord] = useState({ use: '', avoid: '' });
  const [sampleLanguage, setSampleLanguage] = useState<'pl' | 'en'>('pl');
  const [topic, setTopic] = useState('');
  const [sample, setSample] = useState<{ draft: string; current: string; stale: boolean } | null>(
    null
  );
  const [dialog, setDialog] = useState<Dialog>(null);
  const [updateDrafts, setUpdateDrafts] = useState(false);
  const [fontsReady, setFontsReady] = useState<Record<string, boolean>>({});
  const [editingBasics, setEditingBasics] = useState(false);
  const [basics, setBasics] = useState({ positioning: '', audience: '', products: '', facts: '' });
  const [exampleDraft, setExampleDraft] = useState({ postId: '', why: '' });

  useEffect(() => {
    setSample((current) => (current ? { ...current, stale: true } : current));
  }, [brand.draft]);
  useEffect(() => {
    setSample(null);
    setEditingBasics(false);
  }, [route.workspaceId]);
  const draftBarShown = !!brand.draft && canEdit;
  useEffect(() => {
    document.body.classList.toggle('brand-draft-bar-open', draftBarShown);
    return () => document.body.classList.remove('brand-draft-bar-open');
  }, [draftBarShown]);
  useEffect(() => {
    shownBrand.fonts.forEach((font) =>
      loadFont(
        font.family,
        googleFonts.find((item) => item.family === font.family)?.weights ?? [400, 700]
      )
    );
    const recordWhichFontsLoaded = () =>
      setFontsReady(
        Object.fromEntries(
          shownBrand.fonts.map((font) => [
            font.family,
            document.fonts.check(`16px "${font.family}"`),
          ])
        )
      );
    void document.fonts.ready.then(recordWhichFontsLoaded);
    const fallbackCheckTimeoutId = window.setTimeout(
      recordWhichFontsLoaded,
      FONT_CHECK_FALLBACK_DELAY_MS
    );
    return () => window.clearTimeout(fallbackCheckTimeoutId);
  }, [shownBrand.fonts]);

  const updateDraft = (change: (draft: BrandSettings) => BrandSettings) => {
    if (canEdit) commands.saveBrandDraft(workspace.id, change, actor);
  };
  function copyHex(hex: string) {
    void navigator.clipboard?.writeText(hex).catch(() => undefined);
    notify(translate('{hex} copied', { hex }));
  }
  function addWord(kind: 'use' | 'avoid') {
    const value = newWord[kind].trim();
    if (!value || shownBrand.words[kind].includes(value)) return;
    updateDraft((brandDraft) => ({
      ...brandDraft,
      words: { ...brandDraft.words, [kind]: [...brandDraft.words[kind], value] },
    }));
    setNewWord({ ...newWord, [kind]: '' });
  }

  function chooseFont(font: BrandFont) {
    const replacing = dialog?.kind === 'font' ? dialog.replacing : undefined;
    updateDraft((brandDraft) => ({
      ...brandDraft,
      fonts: replacing
        ? brandDraft.fonts.map((item) =>
            item.family === replacing.family && item.role === replacing.role ? font : item
          )
        : [
            ...brandDraft.fonts.filter(
              (item) => !(item.family === font.family && item.role === font.role)
            ),
            font,
          ],
    }));
    setDialog(null);
    notify(
      translate('{family} set for {role} in the draft', {
        family: font.family,
        role: translate(font.role),
      })
    );
  }
  function removeFont(font: BrandFont) {
    const required = font.role === 'Headings' || font.role === 'Body text';
    if (required && shownBrand.fonts.filter((item) => item.role === font.role).length === 1) {
      setDialog({
        kind: 'font',
        role: font.role,
        replacing: font,
        note: translate('{role} need a font. Choose a replacement for {family}.', {
          role: translate(font.role),
          family: font.family,
        }),
      });
      return;
    }
    updateDraft((brandDraft) => ({
      ...brandDraft,
      fonts: brandDraft.fonts.filter(
        (item) => !(item.family === font.family && item.role === font.role)
      ),
    }));
    notify(translate('{family} removed from the draft', { family: font.family }));
  }

  function saveColour(colour: BrandColour) {
    const original = dialog?.kind === 'colour' ? dialog.colour : undefined;
    updateDraft((brandDraft) => {
      const colours = original
        ? brandDraft.colours.map((item) => (item.name === original.name ? colour : item))
        : [...brandDraft.colours, colour];
      const pairs =
        original && original.name !== colour.name
          ? brandDraft.pairs.map(
              ([foregroundName, backgroundName, use]) =>
                [
                  foregroundName === original.name ? colour.name : foregroundName,
                  backgroundName === original.name ? colour.name : backgroundName,
                  use,
                ] as [string, string, string]
            )
          : brandDraft.pairs;
      return { ...brandDraft, colours, pairs };
    });
    setDialog(null);
    notify(
      translate(original ? '{name} updated in the draft' : '{name} added to the draft', {
        name: colour.name,
      })
    );
  }
  function removeColour(name: string) {
    updateDraft((brandDraft) => ({
      ...brandDraft,
      colours: brandDraft.colours.filter((item) => item.name !== name),
      pairs: brandDraft.pairs.filter(
        ([foregroundName, backgroundName]) => foregroundName !== name && backgroundName !== name
      ),
    }));
    setDialog(null);
    notify(translate('{name} removed from the draft', { name }));
  }

  function addOrReplaceWithPickedFile(items: PickedItem[]) {
    const item = items[0];
    const replaceId = dialog?.kind === 'pickAsset' ? dialog.replaceId : undefined;
    const source = brandAssetSourceFor(item.source);
    if (replaceId) {
      updateDraft((brandDraft) => ({
        ...brandDraft,
        assets: brandDraft.assets.map((asset) =>
          asset.id === replaceId ? { ...asset, src: item.src, file: item.file, source } : asset
        ),
      }));
      setDialog(null);
      notify(translate('Replaced in the draft. Approved posts keep the previous file.'));
      return;
    }
    setDialog({
      kind: 'asset',
      isNew: true,
      asset: {
        id: `asset-${Date.now()}`,
        label: 'Brand reference',
        kind: item.kind === 'Document' ? 'Document' : 'Logo',
        src: item.src,
        source,
        file: item.file,
        bg: 'any',
      },
    });
  }
  function saveAsset(asset: BrandAsset, isNew?: boolean) {
    updateDraft((brandDraft) => ({
      ...brandDraft,
      assets: isNew
        ? [...brandDraft.assets, asset]
        : brandDraft.assets.map((item) => (item.id === asset.id ? asset : item)),
    }));
    setDialog(null);
    notify(
      translate(isNew ? '{name} added to the draft' : '{name} updated in the draft', {
        name: translate(asset.label),
      })
    );
  }
  function removeAsset(id: string) {
    updateDraft((brandDraft) => ({
      ...brandDraft,
      assets: brandDraft.assets.filter((asset) => asset.id !== id),
    }));
    setDialog(null);
    notify(translate('Removed from the brand draft. The file stays in its original place.'));
  }

  function sampleCaption(brandSettings: BrandSettings) {
    const subject = topic.trim();
    const soundsPlayful = brandSettings.tones.includes('playful');
    const soundsProfessional =
      brandSettings.tones.includes('professional') && !brandSettings.tones.includes('friendly');
    const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

    let emojiSuffix = ' ☕🍂✨';
    if (brandSettings.emoji === 'none') emojiSuffix = '';
    else if (brandSettings.emoji === 'few') emojiSuffix = ' ☕';

    const brandHashtag = workspace.name.replace(/\s+/g, '');
    let hashtagLine = `\n#${brandHashtag} #${workspace.city} #Kawa #Cafe`;
    if (brandSettings.hashtags === 'none') hashtagLine = '';
    else if (brandSettings.hashtags === 'branded') hashtagLine = `\n#${brandHashtag}`;

    if (sampleLanguage === 'pl') {
      const postSubject = subject || 'nowe sezonowe ciasto';
      let callToAction = '';
      if (brandSettings.cta === 'invite') callToAction = ' Wpadnij do nas, czekamy przy oknie.';
      else if (brandSettings.cta === 'direct')
        callToAction = ' Zamów w aplikacji lub odwiedź nas dziś.';
      let openingSentence = `${capitalise(postSubject)} już w ${workspace.name}. Przygotowujemy je codziennie rano, powoli i z uwagą.`;
      if (soundsPlayful)
        openingSentence = `Uwaga: ${postSubject} może wywołać nagły przypływ przytulności.`;
      else if (soundsProfessional)
        openingSentence = `Od piątku w ${workspace.name}: ${postSubject}.`;
      return `${openingSentence}${emojiSuffix}${callToAction}${hashtagLine}`;
    }
    const postSubject = subject || 'a new seasonal cake';
    let callToAction = '';
    if (brandSettings.cta === 'invite')
      callToAction = ' Come in, we saved you a seat by the window.';
    else if (brandSettings.cta === 'direct') callToAction = ' Order in the app or visit us today.';
    let openingSentence = `${capitalise(postSubject)} has arrived at ${workspace.name}. We make it every morning, slowly and with care.`;
    if (soundsPlayful) openingSentence = `Warning: ${postSubject} may cause spontaneous cosiness.`;
    else if (soundsProfessional)
      openingSentence = `From Friday at ${workspace.name}: ${postSubject}.`;
    const englishHashtagLine = hashtagLine.replace('#Kawa', '#Coffee');
    return `${openingSentence}${emojiSuffix}${callToAction}${englishHashtagLine}`;
  }
  const writeSamplePost = () =>
    setSample({
      draft: sampleCaption(shownBrand),
      current: sampleCaption(brand.active),
      stale: false,
    });

  function applyDraft() {
    const parts = changes.length
      ? changes.map((change) => ({ key: change.key, vars: change.variables }))
      : [{ key: 'No visible changes' }];
    commands.applyBrand(workspace.id, parts, actor);
    setDialog(null);
    notify(
      updateDrafts
        ? translate('Brand guide v{v} is active. New content and unapproved drafts use it.', {
            v: brand.version + 1,
          })
        : translate('Brand guide v{v} is active for new content.', { v: brand.version + 1 })
    );
  }

  const colourByName = Object.fromEntries(
    shownBrand.colours.map((colour) => [colour.name, colour.hex])
  );
  const colourForRole = (roleName: string, fallback: string) =>
    shownBrand.colours.find((colour) => colour.role === roleName)?.hex ?? fallback;
  const dayMonthFormat = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' });
  const headingFont =
    shownBrand.fonts.find((font) => font.role === 'Headings') ?? shownBrand.fonts[0];
  const bodyFont =
    shownBrand.fonts.find((font) => font.role === 'Body text') ??
    shownBrand.fonts[shownBrand.fonts.length - 1];
  const publishedPosts = posts.filter((post) => post.status === 'Published');
  function restoreAsDraft(version: number) {
    commands.restoreBrandVersion(workspace.id, version, actor);
    setDialog(null);
    notify(
      translate('v{v} copied into a new draft. Review and apply it to use it again.', {
        v: version,
      })
    );
  }

  function assetPreview(asset: BrandAsset) {
    if (asset.kind === 'Document' || !asset.src)
      return (
        <div className="brand-asset-doc">
          <SafeIcon name="FiFileText" size={30} />
          <span>{asset.file}</span>
        </div>
      );
    if (asset.kind === 'Photo')
      return <img src={asset.src} alt={asset.label} className="brand-asset-photo" />;
    return (
      <img
        src={asset.src}
        alt={translate(asset.label)}
        className={`brand-asset-logo ${asset.kind === 'Icon' ? 'is-icon' : ''}`}
      />
    );
  }

  function jumpToSection(sectionId: string) {
    const sectionElement = document.getElementById(sectionId) as HTMLDetailsElement | null;
    if (sectionElement) {
      sectionElement.open = true;
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  const previewBackgroundLabel = (background: AssetPreviewBackground) => {
    if (background === 'checker') return translate('Transparent');
    if (background === 'light') return translate('Light');
    return translate('Dark');
  };

  const assetStageBackground = (asset: BrandAsset) => {
    if (asset.kind === 'Photo' || asset.kind === 'Document') return 'plain';
    if (asset.bg === 'dark' && assetPreviewBackground === 'checker') return 'dark';
    return assetPreviewBackground;
  };

  const contrastLevelLabel = (level: ContrastLevel) => {
    if (level === 'pass') return translate('Passes');
    if (level === 'large') return translate('Large text only');
    return translate('Fails');
  };

  const whoAppliesUpdatesNote = () => {
    if (isContributor) return translate('{name} applies brand updates.', { name: workspace.owner });
    if (readOnly) return translate('Read-only view');
    return translate('The client owner applies brand updates. Your changes are saved as a draft.');
  };

  if (brand.version === 0)
    return (
      <div className="content-stack brand-page">
        <div className="panel calendar-empty">
          <SafeIcon name="FiDroplet" />
          <div>
            <strong>{translate('No brand kit yet')}</strong>
            <p className="muted">
              {translate(
                'Answer a few questions in Getting started and AMY creates your brand kit from your logo, colours, a photo you like and your tone.'
              )}
            </p>
          </div>
          <button className="button primary" onClick={() => navigate({ page: 'welcome' })}>
            {translate('Continue getting started')}
          </button>
        </div>
      </div>
    );
  return (
    <div className={`content-stack brand-page ${brand.draft && canEdit ? 'has-draft-bar' : ''}`}>
      <header className="brand-hero panel">
        <div
          className="brand-hero-logo"
          style={{ background: colourForRole('Background', '#F2EBDD') }}
        >
          <img
            src={
              shownBrand.assets.find((asset) => asset.label === 'Main logo')?.src ??
              shownBrand.assets[0]?.src
            }
            alt={workspace.name}
          />
        </div>
        <div className="brand-hero-text">
          <div className="brand-hero-title">
            <h1>{workspace.name}</h1>
            <span className="status published">
              <SafeIcon name="FiCheck" />
              {translate('Active · v{v}', { v: brand.version })}
            </span>
            {brand.draft && (
              <span className="status ready">
                <SafeIcon name="FiEdit3" />
                {translate('Draft v{v} · not used for new posts yet', { v: brand.version + 1 })}
              </span>
            )}
          </div>
          <p>{shownBrand.oneLine}</p>
          <p className="brand-hero-note">
            <SafeIcon name="FiInfo" size={14} />
            {brand.draft
              ? translate(
                  'You are editing a draft{by}. Changes save automatically; AMY keeps using v{v} until the draft is applied.',
                  { by: brand.draftBy ? ` (${brand.draftBy})` : '', v: brand.version }
                )
              : translate('AMY uses this brand guide when creating and revising your content.')}
          </p>
        </div>
        <div className="brand-hero-actions">
          {isContributor ? (
            <button
              className="button"
              onClick={() => showPlannedFeatureNotice('Suggest a brand change')}
              {...readOnlyProps}
            >
              <SafeIcon name="FiMessageSquare" />
              {translate('Suggest a change')}
            </button>
          ) : (
            brand.draft &&
            canEdit && (
              <button
                className="button"
                onClick={() => {
                  commands.discardBrandDraft(workspace.id);
                  notify(translate('Draft discarded. The active guide is unchanged.'));
                }}
              >
                <SafeIcon name="FiRotateCcw" />
                {translate('Discard draft')}
              </button>
            )
          )}
        </div>
      </header>

      <nav className="brand-jump" aria-label={translate('Brand sections')}>
        {[
          ['brand-basics', 'Brand basics'],
          ['brand-visual', 'Visual style'],
          ['brand-voice', 'How AMY writes'],
          ['brand-examples', 'Examples & history'],
        ].map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(event) => {
              event.preventDefault();
              jumpToSection(id);
            }}
          >
            {translate(label)}
          </a>
        ))}
      </nav>

      <div className="brand-layout">
        <div className="brand-main">
          <Section
            id="brand-basics"
            icon="FiCoffee"
            title={translate('Brand basics')}
            intro={translate('Who you are and the facts AMY can rely on.')}
            action={
              canEdit &&
              !editingBasics && (
                <button
                  className="button small"
                  onClick={() => {
                    setBasics({
                      positioning: shownBrand.positioning,
                      audience: shownBrand.audience,
                      products: shownBrand.products,
                      facts: shownBrand.facts,
                    });
                    setEditingBasics(true);
                  }}
                >
                  <SafeIcon name="FiEdit2" size={14} />
                  {translate('Edit')}
                </button>
              )
            }
          >
            {editingBasics ? (
              <form
                className="brand-basics-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  updateDraft((brandDraft) => ({ ...brandDraft, ...basics }));
                  setEditingBasics(false);
                  notify(translate('Saved to the brand draft'));
                }}
              >
                {(
                  [
                    ['positioning', 'In one sentence'],
                    ['facts', 'Verified facts'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key}>
                    <span>{translate(label)}</span>
                    <textarea
                      className="text-field"
                      rows={2}
                      value={basics[key]}
                      onChange={(event) => setBasics({ ...basics, [key]: event.target.value })}
                    />
                  </label>
                ))}
                <div className="button-row">
                  <button className="button primary" type="submit">
                    <SafeIcon name="FiCheck" />
                    {translate('Save to draft')}
                  </button>
                  <button className="button" type="button" onClick={() => setEditingBasics(false)}>
                    {translate('Cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <dl className="brand-facts">
                <div>
                  <dt>{translate('In one sentence')}</dt>
                  <dd>{shownBrand.positioning}</dd>
                </div>
                <div>
                  <dt>{translate('Who we talk to')}</dt>
                  <dd>
                    {business.audience}{' '}
                    <span className="muted small-print">
                      · {translate('from Business details')}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>{translate('Key products')}</dt>
                  <dd>
                    {business.products}{' '}
                    <span className="muted small-print">
                      · {translate('from Business details')}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>{translate('Verified facts')}</dt>
                  <dd>{shownBrand.facts}</dd>
                </div>
              </dl>
            )}
            <p className="muted brand-small">
              <SafeIcon name="FiClock" size={13} />{' '}
              {translate(
                'Audience, products, opening hours and contacts live in one place, Business details, so they are never out of step.'
              )}{' '}
              <button
                className="link-button"
                onClick={() => navigate({ page: 'settings', id: 'business' })}
              >
                {translate('Edit business details')}
              </button>
            </p>
          </Section>

          <Section
            id="brand-visual"
            icon="FiDroplet"
            title={translate('Visual style')}
            intro={translate('Logos, colours, fonts and photo style.')}
            defaultOpen={!isPhone}
          >
            <div className="brand-subhead">
              <h3>{translate('Brand assets')}</h3>
              <div
                className="segment brand-pill-group"
                role="group"
                aria-label={translate('Preview background')}
              >
                {(['checker', 'light', 'dark'] as AssetPreviewBackground[]).map((background) => (
                  <button
                    key={background}
                    className={assetPreviewBackground === background ? 'selected' : ''}
                    aria-pressed={assetPreviewBackground === background}
                    onClick={() => setAssetPreviewBackground(background)}
                  >
                    {previewBackgroundLabel(background)}
                  </button>
                ))}
              </div>
            </div>
            <div className="brand-assets">
              {shownBrand.assets.map((asset) => (
                <figure className="brand-asset" key={asset.id}>
                  <div className={`brand-asset-stage bg-${assetStageBackground(asset)}`}>
                    {assetPreview(asset)}
                  </div>
                  <figcaption>
                    <strong>{translate(asset.label)}</strong>
                    <span className="muted">{asset.note ?? asset.file}</span>
                    <span className="brand-asset-meta">
                      <span
                        className={`status ${asset.source === 'Uploaded' ? 'scheduled' : 'neutral'}`}
                      >
                        <SafeIcon name={assetSourceIcon(asset.source)} size={11} />
                        {translate(asset.source === 'Drive' ? 'Linked' : asset.source)}
                      </span>
                      {asset.bg !== 'any' && (
                        <span className="muted">
                          {translate(
                            asset.bg === 'light'
                              ? 'Use on light backgrounds'
                              : 'Use on dark backgrounds'
                          )}
                        </span>
                      )}
                    </span>
                    {canEdit && (
                      <span className="button-row brand-asset-actions">
                        <button
                          className="button small"
                          onClick={() => setDialog({ kind: 'asset', asset })}
                        >
                          <SafeIcon name="FiEdit2" size={13} />
                          {translate('Edit')}
                        </button>
                        <button
                          className="button small"
                          onClick={() => setDialog({ kind: 'pickAsset', replaceId: asset.id })}
                        >
                          <SafeIcon name="FiRefreshCw" size={13} />
                          {translate('Replace')}
                        </button>
                      </span>
                    )}
                  </figcaption>
                </figure>
              ))}
              <button
                className="brand-asset brand-asset-add"
                onClick={() => setDialog({ kind: 'pickAsset' })}
                {...editLockProps}
              >
                <SafeIcon name="FiPlus" size={22} />
                <span>{translate('Add brand file')}</span>
              </button>
            </div>
            <p className="muted brand-small">
              <SafeIcon name="FiImage" size={13} />{' '}
              <button className="link-button" onClick={() => navigate({ page: 'materials' })}>
                {translate('Campaign photos and videos live in the Material bank.')}
              </button>
            </p>

            <div className="brand-subhead">
              <h3>{translate('Colours')}</h3>
              <button
                className="button small"
                onClick={() => setDialog({ kind: 'colour' })}
                {...editLockProps}
              >
                <SafeIcon name="FiPlus" size={14} />
                {translate('Add colour')}
              </button>
            </div>
            <div className="brand-swatches">
              {shownBrand.colours.map((colour) => (
                <div className="brand-swatch" key={colour.name}>
                  <button
                    className="brand-swatch-chip"
                    style={{ background: colour.hex }}
                    onClick={() => canEdit && setDialog({ kind: 'colour', colour })}
                    aria-label={
                      canEdit ? translate('Edit {name}', { name: colour.name }) : colour.name
                    }
                    disabled={!canEdit}
                  />
                  <div className="brand-swatch-info">
                    <strong>{colour.name}</strong>
                    <span className="muted">
                      {translate(colour.role)}
                      {colour.note ? ` · ${colour.note}` : ''}
                    </span>
                    <button
                      className="brand-hex"
                      onClick={() => copyHex(colour.hex)}
                      aria-label={translate('Copy {hex}', { hex: colour.hex })}
                    >
                      <code>{colour.hex}</code>
                      <SafeIcon name="FiCopy" size={13} />
                    </button>
                    <span className="muted brand-rgb">RGB {rgbText(colour.hex)}</span>
                    {canEdit && (
                      <button
                        className="link-button"
                        onClick={() => setDialog({ kind: 'colour', colour })}
                      >
                        <SafeIcon name="FiEdit2" size={12} /> {translate('Edit')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <h3>{translate('Readability checks')}</h3>
            <p className="muted brand-small">
              {translate(
                'Contrast between real text and background pairs. AA needs 4.5:1 for normal text and 3:1 for large text.'
              )}
            </p>
            <div className="brand-pairs">
              {shownBrand.pairs
                .filter(
                  ([foregroundName, backgroundName]) =>
                    colourByName[foregroundName] && colourByName[backgroundName]
                )
                .map(([foregroundName, backgroundName, use]) => {
                  const ratio = contrastRatio(
                    colourByName[foregroundName],
                    colourByName[backgroundName]
                  );
                  const level = contrastLevelFor(ratio);
                  return (
                    <div className="brand-pair" key={`${foregroundName}-${backgroundName}`}>
                      <div
                        className="brand-pair-sample"
                        style={{
                          color: colourByName[foregroundName],
                          background: colourByName[backgroundName],
                          fontFamily: fontStack(headingFont.family, headingFont.category),
                        }}
                      >
                        Aa
                      </div>
                      <div className="brand-pair-info">
                        <strong>
                          {translate('{fg} on {bg}', { fg: foregroundName, bg: backgroundName })}
                        </strong>
                        <span className="muted">{use}</span>
                      </div>
                      <span className={`status ${contrastLevelStatusClass[level]}`}>
                        <SafeIcon name={contrastLevelIcon[level]} size={11} />
                        {ratio.toFixed(1)}:1 · {contrastLevelLabel(level)}
                      </span>
                    </div>
                  );
                })}
            </div>

            <div className="brand-subhead">
              <h3>{translate('Fonts')}</h3>
              <button
                className="button small"
                onClick={() => setDialog({ kind: 'font', role: 'Accent' })}
                {...editLockProps}
              >
                <SafeIcon name="FiPlus" size={14} />
                {translate('Add font')}
              </button>
            </div>
            <div className="brand-fonts">
              {shownBrand.fonts.map((font) => (
                <div className="brand-font" key={font.family + font.role}>
                  <div className="brand-font-head">
                    <span>
                      <strong>{font.family}</strong>{' '}
                      <span className="muted">· {translate(font.role)} · Google Fonts</span>
                    </span>
                    <span className="button-row">
                      {fontsReady[font.family] === false && (
                        <span className="status review">
                          <SafeIcon name="FiAlertTriangle" size={11} />
                          {translate('Font not loaded, showing a fallback')}
                        </span>
                      )}
                      {canEdit && (
                        <>
                          <button
                            className="button small"
                            onClick={() =>
                              setDialog({ kind: 'font', role: font.role, replacing: font })
                            }
                          >
                            {translate('Change')}
                          </button>
                          <button className="button small ghost" onClick={() => removeFont(font)}>
                            {translate('Remove')}
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  <p
                    className="brand-pangram"
                    style={{ fontFamily: fontStack(font.family, font.category) }}
                  >
                    The quick brown fox jumps over the lazy dog
                  </p>
                  <p
                    className="brand-pangram-pl"
                    style={{ fontFamily: fontStack(font.family, font.category) }}
                  >
                    Pchnąć w tę łódź jeża lub ośm skrzyń fig
                  </p>
                  <p
                    className="brand-font-glyphs"
                    style={{ fontFamily: fontStack(font.family, font.category) }}
                  >
                    Aa Bb Cc Ąą Ęę Łł Óó Śś Żż Źź 0123456789 &amp; ? ! €
                  </p>
                </div>
              ))}
            </div>
            <div
              className="brand-type-sample"
              style={{
                background: colourForRole('Background', '#F2EBDD'),
                color: colourForRole('Text', '#26312D'),
              }}
            >
              <span
                style={{
                  color: colourForRole('Accent', '#B5653A'),
                  fontFamily: fontStack(bodyFont.family, bodyFont.category),
                }}
                className="brand-type-kicker"
              >
                {workspace.name}
              </span>
              <h4
                style={{
                  fontFamily: fontStack(headingFont.family, headingFont.category),
                  color: colourForRole('Primary', '#365A48'),
                }}
              >
                {shownBrand.positioning}
              </h4>
              <p style={{ fontFamily: fontStack(bodyFont.family, bodyFont.category) }}>
                {business.products}
              </p>
            </div>

            <div className="two-column brand-rules">
              <div>
                <h3>{translate('Photo style')}</h3>
                <ListEditor
                  items={shownBrand.photoStyle}
                  canEdit={canEdit}
                  onChange={(items) =>
                    updateDraft((brandDraft) => ({ ...brandDraft, photoStyle: items }))
                  }
                  placeholder={translate('Add a photo guideline')}
                  label={translate('Photo style')}
                />
              </div>
              <div>
                <h3>{translate('Logo use')}</h3>
                <ListEditor
                  items={shownBrand.logoRules}
                  canEdit={canEdit}
                  onChange={(items) =>
                    updateDraft((brandDraft) => ({ ...brandDraft, logoRules: items }))
                  }
                  placeholder={translate('Add a logo rule')}
                  label={translate('Logo use')}
                />
              </div>
            </div>
          </Section>

          <Section
            id="brand-voice"
            icon="FiFeather"
            title={translate('How AMY writes')}
            intro={translate(
              'Choose how your posts should sound. AMY follows this for every caption.'
            )}
            defaultOpen={!isPhone}
          >
            <h3>{translate('Tone of voice')}</h3>
            <p className="muted brand-small">{translate('Choose one or more.')}</p>
            <div className="brand-tones">
              {toneOptions.map((option) => {
                const isSelected = shownBrand.tones.includes(option.id);
                return (
                  <button
                    key={option.id}
                    className={`brand-tone ${isSelected ? 'selected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() =>
                      updateDraft((brandDraft) => ({
                        ...brandDraft,
                        tones: isSelected
                          ? brandDraft.tones.filter((id) => id !== option.id)
                          : [...brandDraft.tones, option.id],
                      }))
                    }
                    {...editLockProps}
                  >
                    <span className="brand-tone-head">
                      <SafeIcon name={isSelected ? 'FiCheckCircle' : 'FiCircle'} size={16} />
                      <strong>{translate(option.label)}</strong>
                    </span>
                    <span className="muted">“{translate(option.example)}”</span>
                  </button>
                );
              })}
            </div>

            <div className="brand-choices">
              {choiceGroups.map((group) => (
                <fieldset key={group.key} className="brand-choice">
                  <legend>{translate(group.label)}</legend>
                  <div className="segment brand-pill-group">
                    {group.options.map(([value, label]) => {
                      const isSelected = shownBrand[group.key] === value;
                      return (
                        <button
                          key={value}
                          className={isSelected ? 'selected' : ''}
                          aria-pressed={isSelected}
                          onClick={() =>
                            updateDraft((brandDraft) => ({ ...brandDraft, [group.key]: value }))
                          }
                          {...editLockProps}
                        >
                          {isSelected && <SafeIcon name="FiCheck" size={13} />}
                          {translate(label)}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>

            <div className="two-column">
              {(['use', 'avoid'] as const).map((kind) => (
                <div key={kind} className="brand-words">
                  <h3>{translate(kind === 'use' ? 'Words we use' : 'Words we avoid')}</h3>
                  <div className="brand-word-list">
                    {shownBrand.words[kind].map((word) => (
                      <span className={`brand-word ${kind}`} key={word}>
                        {word}
                        {canEdit && (
                          <button
                            aria-label={translate('Remove {word}', { word })}
                            onClick={() =>
                              updateDraft((brandDraft) => ({
                                ...brandDraft,
                                words: {
                                  ...brandDraft.words,
                                  [kind]: brandDraft.words[kind].filter((item) => item !== word),
                                },
                              }))
                            }
                          >
                            <SafeIcon name="FiX" size={12} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  {canEdit && (
                    <form
                      className="brand-word-add"
                      onSubmit={(event) => {
                        event.preventDefault();
                        addWord(kind);
                      }}
                    >
                      <input
                        className="text-field"
                        value={newWord[kind]}
                        onChange={(event) => setNewWord({ ...newWord, [kind]: event.target.value })}
                        placeholder={translate('Add a word or phrase')}
                        aria-label={translate(kind === 'use' ? 'Words we use' : 'Words we avoid')}
                      />
                      <button className="button small" type="submit">
                        <SafeIcon name="FiPlus" size={14} />
                        {translate('Add')}
                      </button>
                    </form>
                  )}
                </div>
              ))}
            </div>

            <h3>{translate('Content languages')}</h3>
            <p className="muted brand-small">
              {translate(
                'Rules for each language AMY writes in. The app language does not change your content.'
              )}
            </p>
            <div className="brand-langs">
              {shownBrand.languages.map((item, index) => (
                <div className="brand-lang" key={item.code}>
                  <span className="tag">{item.code}</span>
                  <div style={{ flex: 1 }}>
                    <strong>{item.title}</strong>
                    {canEdit ? (
                      <input
                        className="text-field brand-lang-note"
                        value={item.note}
                        aria-label={translate('Notes for {lang}', { lang: item.code })}
                        onChange={(event) =>
                          updateDraft((brandDraft) => ({
                            ...brandDraft,
                            languages: brandDraft.languages.map((language, languageIndex) =>
                              languageIndex === index
                                ? { ...language, note: event.target.value }
                                : language
                            ),
                          }))
                        }
                      />
                    ) : (
                      <div className="muted">{item.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <label htmlFor="brand-notes">
              <h3>{translate('Anything else AMY should know?')}</h3>
            </label>
            <textarea
              id="brand-notes"
              className="text-field brand-notes"
              rows={4}
              maxLength={MAX_NOTES}
              value={shownBrand.notes}
              onChange={(event) =>
                updateDraft((brandDraft) => ({ ...brandDraft, notes: event.target.value }))
              }
              {...editLockProps}
            />
            <div className="brand-notes-foot">
              <span className="muted">
                <SafeIcon name="FiShield" size={13} />{' '}
                {translate(
                  'These notes guide writing only. They cannot change permissions, approvals or platform rules.'
                )}
              </span>
              <span
                className={`muted ${shownBrand.notes.length > NOTES_NEAR_LIMIT ? 'brand-count-warn' : ''}`}
              >
                {shownBrand.notes.length.toLocaleString(language)} /{' '}
                {MAX_NOTES.toLocaleString(language)}
              </span>
            </div>
            {shownBrand.tones.includes('playful') && shownBrand.tones.includes('professional') && (
              <div className="notice-banner brand-conflict">
                <SafeIcon name="FiAlertTriangle" />
                <span>
                  {translate(
                    '"Playful" and "Clear and professional" can pull in different directions. AMY will ask which one wins when they conflict.'
                  )}
                </span>
              </div>
            )}
            <button
              className="button brand-try-jump"
              onClick={() =>
                document
                  .getElementById('brand-try')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            >
              <SafeIcon name="FiZap" />
              {translate('Try this style')}
            </button>
          </Section>

          <Section
            id="brand-examples"
            icon="FiStar"
            title={translate('Examples & history')}
            intro={translate('What good looks like, and what to avoid.')}
            defaultOpen={!isPhone}
          >
            <div className="two-column">
              <div>
                <div className="brand-subhead">
                  <h3>
                    <SafeIcon name="FiThumbsUp" size={15} /> {translate('Good examples')}
                  </h3>
                  {canEdit && (
                    <button
                      className="button small"
                      onClick={() => {
                        setExampleDraft({ postId: publishedPosts[0]?.id ?? '', why: '' });
                        setDialog({ kind: 'addExample' });
                      }}
                    >
                      <SafeIcon name="FiPlus" size={14} />
                      {translate('Add example')}
                    </button>
                  )}
                </div>
                {shownBrand.examples.map((item, index) => (
                  <button
                    className="checklist-item brand-example list-button"
                    key={item.name}
                    onClick={() => setDialog({ kind: 'example', index })}
                  >
                    <img src={item.image} className="post-thumb" alt="" />
                    <div style={{ flex: 1 }}>
                      <strong>{item.name}</strong>
                      <div className="muted">{item.why}</div>
                      <div className="muted brand-small">
                        {translate('Approved by {name} · {date}', {
                          name: item.by,
                          date: dayMonthFormat.format(demoNovemberDate(item.day)),
                        })}
                      </div>
                    </div>
                    <SafeIcon name="FiChevronRight" />
                  </button>
                ))}
              </div>
              <div>
                <h3>
                  <SafeIcon name="FiThumbsDown" size={15} /> {translate('Avoid')}
                </h3>
                {shownBrand.avoid.map((item, index) => (
                  <div className="checklist-item brand-example" key={item.text}>
                    <span className={`brand-avoid-dot ${item.rule ? 'rule' : ''}`}>
                      <SafeIcon name={item.rule ? 'FiSlash' : 'FiMessageSquare'} size={14} />
                    </span>
                    <div style={{ flex: 1 }}>
                      <strong>{item.text}</strong>
                      <div className="muted">{item.why}</div>
                      {!item.rule && canEdit && (
                        <button
                          className="link-button"
                          onClick={() => {
                            updateDraft((brandDraft) => ({
                              ...brandDraft,
                              avoid: brandDraft.avoid.map((avoidItem, avoidIndex) =>
                                avoidIndex === index
                                  ? {
                                      ...avoidItem,
                                      rule: true,
                                      why: avoidItem.why.replace(
                                        'One-off feedback, not a rule.',
                                        'Saved as a brand rule.'
                                      ),
                                    }
                                  : avoidItem
                              ),
                            }));
                            notify(translate('Saved as a proposed brand rule in the draft'));
                          }}
                        >
                          {translate('Make this a brand rule')}
                        </button>
                      )}
                    </div>
                    <span className={`status ${item.rule ? 'input' : 'neutral'}`}>
                      {translate(item.rule ? 'Brand rule' : 'One-off')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <details className="brand-history">
              <summary>
                <SafeIcon name="FiClock" size={15} /> {translate('Change history')}
              </summary>
              {brand.draft && (
                <div className="checklist-item">
                  <SafeIcon name="FiEdit3" />
                  <div>
                    <strong>{translate('v{v} · Draft', { v: brand.version + 1 })}</strong>
                    <div className="muted">
                      {brand.draftBy ? `${brand.draftBy} · ` : ''}
                      {changes.length
                        ? changes.map((change) => change.label).join(', ')
                        : translate('No changes yet')}
                    </div>
                  </div>
                </div>
              )}
              {[...brand.history].reverse().map((item) => (
                <div className="checklist-item" key={item.version}>
                  <SafeIcon name={item.version === brand.version ? 'FiCheckCircle' : 'FiArchive'} />
                  <div style={{ flex: 1 }}>
                    <strong>
                      {translate(
                        item.version === brand.version ? 'v{v} · Active' : 'v{v} · Replaced',
                        {
                          v: item.version,
                        }
                      )}
                    </strong>
                    <div className="muted">
                      {translate('Applied {date} by {name}', {
                        date: dayMonthFormat.format(demoNovemberDate(item.day)),
                        name: item.by,
                      })}{' '}
                      ·{' '}
                      {item.parts
                        ? item.parts.map((part) => translate(part.key, part.variables)).join(', ')
                        : translate(item.summary)}
                    </div>
                  </div>
                  {item.version !== brand.version && item.snapshot && canEdit && (
                    <button
                      className="button small"
                      onClick={() =>
                        brand.draft
                          ? setDialog({ kind: 'restore', version: item.version })
                          : restoreAsDraft(item.version)
                      }
                    >
                      <SafeIcon name="FiRotateCcw" size={13} />
                      {translate('Restore as draft')}
                    </button>
                  )}
                </div>
              ))}
            </details>
          </Section>
        </div>

        <aside className="brand-aside">
          <div className="panel brand-status">
            <h3>{translate('Brand guide status')}</h3>
            <div className="brand-status-row">
              <span className="status published">
                <SafeIcon name="FiCheck" size={11} />v{brand.version}
              </span>
              <span>
                {translate('Active since {date}', {
                  date: dayMonthFormat.format(
                    demoNovemberDate(brand.history[brand.history.length - 1].day)
                  ),
                })}
              </span>
            </div>
            {brand.draft ? (
              <>
                <div className="brand-status-row">
                  <span className="status ready">
                    <SafeIcon name="FiEdit3" size={11} />v{brand.version + 1}
                  </span>
                  <span>{translate('Draft · changes: {n}', { n: changes.length })}</span>
                </div>
                <ul className="brand-change-list">
                  {changes.map((change) => (
                    <li key={change.label}>{change.label}</li>
                  ))}
                </ul>
                {canApply ? (
                  <button
                    className="button primary"
                    style={{ width: '100%' }}
                    disabled={!changes.length}
                    onClick={() => setDialog({ kind: 'review' })}
                  >
                    <SafeIcon name="FiCheckSquare" />
                    {translate('Review and apply')}
                  </button>
                ) : (
                  <p className="muted brand-small">{whoAppliesUpdatesNote()}</p>
                )}
              </>
            ) : (
              <p className="muted brand-small">
                {canEdit
                  ? translate(
                      'Change anything on this page to start a draft. AMY keeps using the active version until it is applied.'
                    )
                  : translate('No draft in progress.')}
              </p>
            )}
          </div>

          <div className="panel brand-try" id="brand-try">
            <h3>
              <SafeIcon name="FiZap" size={15} /> {translate('Try this style')}
            </h3>
            <p className="muted brand-small">
              {translate('See how AMY would write a post with the draft settings.')}
            </p>
            <label className="brand-topic">
              <span className="muted">
                {translate('What should the post be about? (optional)')}
              </span>
              <input
                className="text-field"
                value={topic}
                onChange={(event) => setTopic(event.target.value)}
                placeholder={translate('For example: Saturday coffee tasting')}
              />
            </label>
            <div className="segment" role="group" aria-label={translate('Language')}>
              {(['pl', 'en'] as const).map((code) => (
                <button
                  key={code}
                  className={sampleLanguage === code ? 'selected' : ''}
                  aria-pressed={sampleLanguage === code}
                  onClick={() => {
                    setSampleLanguage(code);
                    setSample(null);
                  }}
                >
                  {code === 'pl' ? 'Polski' : 'English'}
                </button>
              ))}
            </div>
            <button
              className="button"
              style={{ width: '100%', marginTop: 10 }}
              onClick={writeSamplePost}
            >
              <SafeIcon name="FiZap" />
              {translate(sample ? 'Write a new sample' : 'Write a sample post')}
            </button>
            {sample && (
              <div className="brand-sample">
                <span className="tag">{translate('Sample preview · not a scheduled post')}</span>
                {sample.stale && (
                  <p className="status review brand-small">
                    <SafeIcon name="FiRefreshCw" size={11} />
                    {translate('Settings changed. Write a new sample to see the difference.')}
                  </p>
                )}
                <p className="brand-sample-label">
                  {translate(brand.draft ? 'With your draft' : 'With the active style')}
                </p>
                <p className="brand-sample-text">{sample.draft}</p>
                {brand.draft &&
                  (sample.draft === sample.current ? (
                    <p className="muted brand-small">
                      {translate(
                        'Same as the active style. Change a setting to see the difference.'
                      )}
                    </p>
                  ) : (
                    <>
                      <p className="brand-sample-label">{translate('With the active style')}</p>
                      <p className="brand-sample-text muted">{sample.current}</p>
                    </>
                  ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {brand.draft && canEdit && (
        <div className="brand-draft-bar" role="region" aria-label={translate('Draft')}>
          <span>
            <SafeIcon name="FiEdit3" size={14} />{' '}
            {translate('Draft v{v} · changes: {n}', { v: brand.version + 1, n: changes.length })}
          </span>
          <span className="button-row">
            <button
              className="icon-button draft-bar-chat"
              onClick={() => openChat()}
              aria-label={translate('Chat with AMY')}
            >
              <SafeIcon name="FiMessageCircle" />
            </button>
            {canApply ? (
              <button
                className="button primary small"
                disabled={!changes.length}
                onClick={() => setDialog({ kind: 'review' })}
              >
                {translate('Review changes')}
              </button>
            ) : (
              <span className="muted small-print">
                {translate('Saved automatically. {name} reviews and applies it.', {
                  name: workspace.owner,
                })}
              </span>
            )}
          </span>
        </div>
      )}

      {dialog?.kind === 'font' && (
        <FontPicker
          initialRole={dialog.role}
          replacing={dialog.replacing}
          onCancel={() => setDialog(null)}
          onChoose={chooseFont}
        />
      )}
      {dialog?.kind === 'font' && dialog.note && (
        <div className="toast toast-info" role="status">
          <SafeIcon name="FiInfo" /> {dialog.note}
        </div>
      )}
      {dialog?.kind === 'colour' && (
        <ColourEditor
          colour={dialog.colour}
          existing={shownBrand.colours}
          onCancel={() => setDialog(null)}
          onSave={saveColour}
          onRemove={dialog.colour ? () => removeColour(dialog.colour!.name) : undefined}
        />
      )}
      {dialog?.kind === 'pickAsset' && (
        <MaterialPicker
          title={dialog.replaceId ? 'Replace a brand file' : 'Add a brand file'}
          kinds={['Photo', 'Document']}
          onCancel={() => setDialog(null)}
          onPick={addOrReplaceWithPickedFile}
        />
      )}
      {dialog?.kind === 'asset' && (
        <AssetEditor
          asset={dialog.asset}
          isNew={dialog.isNew}
          onCancel={() => setDialog(null)}
          onSave={(asset) => saveAsset(asset, dialog.isNew)}
          onRemove={dialog.isNew ? undefined : () => removeAsset(dialog.asset.id)}
        />
      )}
      {dialog?.kind === 'example' && shownBrand.examples[dialog.index] && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <section
            className="panel modal-card example-card"
            role="dialog"
            aria-modal="true"
            aria-label={shownBrand.examples[dialog.index].name}
          >
            <img
              src={shownBrand.examples[dialog.index].image}
              alt={shownBrand.examples[dialog.index].name}
            />
            <h2>{shownBrand.examples[dialog.index].name}</h2>
            {shownBrand.examples[dialog.index].caption && (
              <div className="example-caption">
                <span className="muted small-print">{translate('Caption')}</span>
                <p>{shownBrand.examples[dialog.index].caption}</p>
              </div>
            )}
            <p>
              <strong>{translate('Why it is a good example')}</strong>
              <br />
              {shownBrand.examples[dialog.index].why}
            </p>
            {shownBrand.examples[dialog.index].postId && (
              <button
                className="link-button"
                onClick={() => {
                  setDialog(null);
                  navigate({ page: 'post', id: shownBrand.examples[dialog.index].postId });
                }}
              >
                {translate('Open post')}
              </button>
            )}
            <p className="muted">
              {translate('Approved by {name} · {date}', {
                name: shownBrand.examples[dialog.index].by,
                date: dayMonthFormat.format(
                  demoNovemberDate(shownBrand.examples[dialog.index].day)
                ),
              })}
            </p>
            <div className="button-row">
              {canEdit && (
                <button
                  className="button danger-outline"
                  onClick={() => {
                    const removedIndex = dialog.index;
                    updateDraft((brandDraft) => ({
                      ...brandDraft,
                      examples: brandDraft.examples.filter(
                        (_, exampleIndex) => exampleIndex !== removedIndex
                      ),
                    }));
                    setDialog(null);
                    notify(translate('Example removed from the draft'));
                  }}
                >
                  {translate('Remove example')}
                </button>
              )}
              <button className="button" onClick={() => setDialog(null)}>
                {translate('Close')}
              </button>
            </div>
          </section>
        </div>
      )}
      {dialog?.kind === 'restore' && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <section
            className="panel modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="restore-title"
          >
            <h2 id="restore-title">{translate('Replace the current draft?')}</h2>
            <p>
              {translate(
                'There is an unapplied draft by {name}. Restoring v{v} replaces it, and its changes are lost.',
                { name: brand.draftBy ?? workspace.owner, v: dialog.version }
              )}
            </p>
            <div className="button-row">
              <button
                className="button"
                onClick={() => {
                  setDialog({ kind: 'review' });
                }}
              >
                {translate('Review the draft first')}
              </button>
              <button className="button danger" onClick={() => restoreAsDraft(dialog.version)}>
                {translate('Replace draft with v{v}', { v: dialog.version })}
              </button>
              <button className="button ghost" onClick={() => setDialog(null)}>
                {translate('Keep my draft')}
              </button>
            </div>
          </section>
        </div>
      )}
      {dialog?.kind === 'addExample' && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <section
            className="panel modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-example-title"
          >
            <h2 id="add-example-title">{translate('Add a good example')}</h2>
            <label htmlFor="example-post">{translate('Published post')}</label>
            <select
              id="example-post"
              className="select-field"
              value={exampleDraft.postId}
              onChange={(event) => setExampleDraft({ ...exampleDraft, postId: event.target.value })}
            >
              {publishedPosts.map((post) => (
                <option key={post.id} value={post.id}>
                  {post.title} · {post.channel}
                </option>
              ))}
            </select>
            <label htmlFor="example-why" style={{ display: 'block', marginTop: 12 }}>
              {translate('Why is it a good example?')}
            </label>
            <textarea
              id="example-why"
              className="text-field"
              rows={3}
              value={exampleDraft.why}
              onChange={(event) => setExampleDraft({ ...exampleDraft, why: event.target.value })}
              placeholder={translate('For example: short, warm and shows real guests')}
            />
            <div className="button-row" style={{ marginTop: 14 }}>
              <button
                className="button primary"
                disabled={!exampleDraft.postId || !exampleDraft.why.trim()}
                onClick={() => {
                  const post = posts.find((item) => item.id === exampleDraft.postId);
                  if (!post) return;
                  updateDraft((brandDraft) => ({
                    ...brandDraft,
                    examples: [
                      ...brandDraft.examples,
                      {
                        name: `${post.title} · ${post.channel}`,
                        image: post.image,
                        why: exampleDraft.why.trim(),
                        by: actor.name,
                        day: DEMO_DAY_FOR_NEW_EXAMPLES,
                        caption: post.caption,
                        postId: post.id,
                      },
                    ],
                  }));
                  setDialog(null);
                  notify(translate('Example added to the draft'));
                }}
              >
                <SafeIcon name="FiCheck" />
                {translate('Add to draft')}
              </button>
              <button className="button" onClick={() => setDialog(null)}>
                {translate('Cancel')}
              </button>
            </div>
          </section>
        </div>
      )}

      {dialog?.kind === 'review' && (
        <div
          className="drawer-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDialog(null);
          }}
        >
          <section className="drawer" role="dialog" aria-modal="true" aria-labelledby="apply-title">
            <div className="drawer-header">
              <div>
                <h2 id="apply-title">{translate('Apply brand updates')}</h2>
                <div className="muted">
                  {translate('v{next} replaces v{v} for new content.', {
                    next: brand.version + 1,
                    v: brand.version,
                  })}
                  {brand.draftBy
                    ? ` · ${translate('Draft by {name}', { name: brand.draftBy })}`
                    : ''}
                </div>
              </div>
              <button
                className="icon-button"
                onClick={() => setDialog(null)}
                aria-label={translate('Close')}
              >
                <SafeIcon name="FiX" />
              </button>
            </div>
            <h3>{translate('What changes')}</h3>
            <div className="brand-diff">
              {changes.map((change) => (
                <div className="brand-diff-row" key={change.label}>
                  <strong>{change.label}</strong>
                  <span className="brand-diff-from">
                    {change.swatches?.[0] && (
                      <span className="diff-swatch" style={{ background: change.swatches[0] }} />
                    )}
                    {change.from}
                  </span>
                  <SafeIcon name="FiArrowDown" size={14} />
                  <span className="brand-diff-to">
                    {change.swatches?.[1] && (
                      <span className="diff-swatch" style={{ background: change.swatches[1] }} />
                    )}
                    {change.to}
                  </span>
                </div>
              ))}
            </div>
            <div className="notice-banner">
              <SafeIcon name="FiInfo" />
              <span>
                {translate('Approved and scheduled posts keep the style they were approved with.')}
              </span>
            </div>
            <label className="brand-check">
              <input
                type="checkbox"
                checked={updateDrafts}
                onChange={(event) => setUpdateDrafts(event.target.checked)}
              />{' '}
              {translate('Also update drafts that are not approved yet')}
            </label>
            <div className="button-row" style={{ marginTop: 20 }}>
              <button className="button primary" onClick={applyDraft}>
                <SafeIcon name="FiCheck" />
                {translate('Apply brand updates')}
              </button>
              <button className="button" onClick={() => setDialog(null)}>
                {translate('Cancel')}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function AssetEditor({
  asset,
  isNew,
  onCancel,
  onSave,
  onRemove,
}: {
  asset: BrandAsset;
  isNew?: boolean;
  onCancel: () => void;
  onSave: (asset: BrandAsset) => void;
  onRemove?: () => void;
}) {
  const { translate } = useApp();
  const [editedAsset, setEditedAsset] = useState(asset);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section className="drawer" role="dialog" aria-modal="true" aria-labelledby="asset-title">
        <div className="drawer-header">
          <div>
            <h2 id="asset-title">{translate(isNew ? 'Add a brand file' : 'Edit brand file')}</h2>
            <div className="muted">{editedAsset.file}</div>
          </div>
          <button className="icon-button" onClick={onCancel} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div
          className={`brand-asset-stage bg-${editedAsset.bg === 'dark' ? 'dark' : 'checker'} asset-editor-preview`}
        >
          {editedAsset.src ? (
            <img src={editedAsset.src} alt="" className="brand-asset-logo" />
          ) : (
            <SafeIcon name="FiFileText" size={34} />
          )}
        </div>
        <label htmlFor="asset-purpose">{translate('What is it for?')}</label>
        <select
          id="asset-purpose"
          className="select-field"
          value={editedAsset.label}
          onChange={(event) =>
            setEditedAsset({
              ...editedAsset,
              label: event.target.value,
              kind: assetKindForPurpose(event.target.value, editedAsset.kind),
            })
          }
        >
          {assetPurposes.map((item) => (
            <option key={item} value={item}>
              {translate(item)}
            </option>
          ))}
        </select>
        <label htmlFor="asset-bg" style={{ display: 'block', marginTop: 12 }}>
          {translate('Background')}
        </label>
        <select
          id="asset-bg"
          className="select-field"
          value={editedAsset.bg}
          onChange={(event) =>
            setEditedAsset({ ...editedAsset, bg: event.target.value as BrandAsset['bg'] })
          }
        >
          <option value="any">{translate('Any background')}</option>
          <option value="light">{translate('Use on light backgrounds')}</option>
          <option value="dark">{translate('Use on dark backgrounds')}</option>
        </select>
        <label htmlFor="asset-note" style={{ display: 'block', marginTop: 12 }}>
          {translate('Usage note (optional)')}
        </label>
        <input
          id="asset-note"
          className="text-field"
          value={editedAsset.note ?? ''}
          onChange={(event) =>
            setEditedAsset({ ...editedAsset, note: event.target.value || undefined })
          }
          placeholder={translate('For example: profile pictures')}
        />
        <p className="muted small-print">
          {translate('Source')}:{' '}
          {translate(editedAsset.source === 'Drive' ? 'Linked from Drive' : editedAsset.source)}
        </p>
        <div className="button-row" style={{ marginTop: 18 }}>
          <button className="button primary" onClick={() => onSave(editedAsset)}>
            <SafeIcon name="FiCheck" />
            {translate('Save to draft')}
          </button>
          <button className="button" onClick={onCancel}>
            {translate('Cancel')}
          </button>
          {onRemove && !confirmingRemoval && (
            <button className="button danger-outline" onClick={() => setConfirmingRemoval(true)}>
              <SafeIcon name="FiTrash2" />
              {translate('Remove from brand')}
            </button>
          )}
        </div>
        {confirmingRemoval && onRemove && (
          <div className="notice-banner status-panel-inline warn" style={{ marginTop: 12 }}>
            <SafeIcon name="FiAlertTriangle" />
            <span>
              {translate(
                'AMY stops using this file for new posts. The file itself is not deleted.'
              )}
            </span>
            <button className="button small danger" onClick={onRemove}>
              {translate('Remove')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
